import { Injectable, inject } from '@angular/core';
import { AppData } from '@shared/models/appdata.model';
import { ApplicationInsights, IExceptionTelemetry, SeverityLevel } from '@microsoft/applicationinsights-web';
import { Environment } from '../models/environment.model';

@Injectable({ providedIn: 'root' })
export class AppInsightsService {
  private appData = inject(AppData);

  private appInsights: ApplicationInsights;

  constructor() {
    const appData = this.appData;

    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: appData.aiIKey,
        enableAutoRouteTracking: true,
        enableCorsCorrelation: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        enableDebug: this.appData.environment === Environment.Development,
        disableExceptionTracking: false,
        disableAjaxTracking: false,
        enableUnhandledPromiseRejectionTracking: true,
        // Chrome's permissions policy blocks the deprecated 'unload' event; 'beforeunload' and 'pagehide' still flush telemetry.
        disablePageUnloadEvents: ['unload'],
      },
    });

    if (appData.aiIKey) {
      this.appInsights.loadAppInsights();
    }
  }

  setAuthenticatedUserContext(authenticatedUserId: string, dealerId?: string): void {
    this.appInsights.setAuthenticatedUserContext(authenticatedUserId, dealerId, true);
  }

  clearAuthenticatedUserContext(): void {
    this.appInsights.clearAuthenticatedUserContext();
  }

  trackPage(name?: string, uri?: string): void {
    this.appInsights.trackPageView({ name, uri });
  }

  trackEvent(name: string, properties?: { [key: string]: unknown }): void {
    this.appInsights.trackEvent({ name }, properties);
  }

  trackError(error: Error | string, properties?: { [key: string]: unknown }): void {
    const exception: IExceptionTelemetry = {
      exception: error instanceof Error ? error : new Error(error),
      severityLevel: SeverityLevel.Error,
      properties,
    };
    this.appInsights.trackException(exception);
  }

  trackWarning(message: string, properties?: { [key: string]: unknown }): void {
    const warning: IExceptionTelemetry = {
      exception: new Error(message),
      severityLevel: SeverityLevel.Warning,
      properties,
    };
    this.appInsights.trackException(warning);
  }

  trackMetric(name: string, value: number, properties?: { [key: string]: unknown }): void {
    this.appInsights.trackMetric({ name, average: value }, properties);
  }

  addTelemetryInitializer(fn: (item: any) => boolean | void): void {
    this.appInsights.addTelemetryInitializer(fn);
  }
}
